import { ApolloCache, gql } from '@apollo/client';
import {
  Permission,
  Resource,
  ResourceAccess,
  ResourceAccessUpdatedResult,
} from '@app/types/blackroom-api';

function updatePermissionsCache(
  cache: ApolloCache<any>,
  updatedResourceAccess: ResourceAccessUpdatedResult,
  removedPermissions: Permission[],
  existingAccess?: ResourceAccess,
) {
  // Remove descendants access
  const resource = cache.readFragment({
    id: cache.identify({
      __typename: 'Resource',
      id: updatedResourceAccess.resourceId,
    }),
    fragment: gql`
      fragment ResourceWithDescendantsAccesses on Resource {
        descendantsAccesses {
          id
        }
      }
    `,
  });
  (resource as Resource | null)?.descendantsAccesses.forEach(
    (descendantAccess) => {
      cache.modify({
        id: cache.identify({
          __typename: 'ResourceAccess',
          id: descendantAccess.id,
        }),
        fields: {
          permissions: (existingPermissions = []) => {
            let updatedPermissions = (
              existingPermissions as Permission[]
            ).filter((p) => !removedPermissions.includes(p));

            // If all view permissions are removed, remove download permission as well (= empty permissions)
            if (
              ![
                Permission.ResourceAccessOriginal,
                Permission.ResourceAccessRedacted,
              ].some((p) => updatedPermissions.includes(p))
            ) {
              updatedPermissions = [];
            }

            return updatedPermissions;
          },
        },
      });
    },
  );

  // Remove access
  if (updatedResourceAccess?.id === null) {
    cache.modify({
      id: cache.identify({
        __typename: 'Resource',
        id: updatedResourceAccess.resourceId,
      }),
      fields: {
        accesses: (existingAccesses = [], { readField }) => {
          return (existingAccesses as ResourceAccess[]).filter(
            (access) => readField('id', access) !== existingAccess?.id,
          );
        },
      },
    });
    return;
  }

  // Update access
  if (existingAccess?.id) {
    cache.modify({
      id: cache.identify({
        __typename: 'ResourceAccess',
        id: existingAccess.id,
      }),
      fields: {
        permissions: () => {
          return updatedResourceAccess?.permissions;
        },
      },
    });
    return;
  }

  // Add access
  cache.modify({
    id: cache.identify({
      __typename: 'Resource',
      id: updatedResourceAccess.resourceId,
    }),
    fields: {
      accesses: (existingAccesses = []) => {
        const newAccess = cache.writeFragment({
          data: updatedResourceAccess,
          fragment: gql`
            fragment NewAccess on ResourceAccess {
              id
              permissions
              resourceId
              groupId
            }
          `,
        });

        return [...existingAccesses, newAccess];
      },
    },
  });
}

export default updatePermissionsCache;
